import React, { useContext, useEffect, useState } from 'react';
import { isEmail, isFullNameValid, isNotNullOrEmpty } from '../../../helpers/Utils';
import ICountry from '../../../models/Country.interface';
import { CheckoutBillingAddressData } from '../../../models/ElavonCheckoutModels';
import { IGiftCardCheckoutResult } from '../../../models/GiftCardCheckoutResult.model';
import { ICardCheckoutModel, ICheckoutSessionData } from '../../../models/PaymentStep.model';
import IState from '../../../models/State.interface';
import DataService from '../../../services/DataService.service';
import { AppSettingContext } from '../../Application';
import LoadingComponent from '../../shared/LoadingComponent';

interface CheckoutPaymentProps {
    basketId: string,
    senderDetails: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
    },
    paymentData: {
        amount: string;
        currency: string;
    },
    handleError(errorMsg: string): void;
    handleFormUpdate(isFormValid: boolean, billingDetails: CheckoutBillingAddressData): void;
}

const ElavonPayment = (props: CheckoutPaymentProps) => {
    const { labels, elavonSettings } = useContext(AppSettingContext);

    //#region STATE PROPERTIES

    const [isLoading, setIsLoading] = useState(false);
    const [checkoutModel, setCheckoutModel] = useState<ICardCheckoutModel>({
        email: props.senderDetails.email,
        country: 'IE',
        county: '',
        city: '',
        postcode: '',
        addressLine1: '',
        addressLine2: '',
        cardNumber: '',
        expiryDate: '',
        cardholder: `${props.senderDetails.firstName} ${props.senderDetails.lastName}`,
        securityCode: '',
    });
    const [errors, setErrors] = useState({
        country: '',
        county: '',
        city: '',
        postcode: '',
        addressLine1: '',
        addressLine2: '',
        cardNumber: '',
        expiryDate: '',
        cardholder: '',
        securityCode: '',
    });
    const [countries, setCountries] = useState<ICountry[]>([] as ICountry[]);
    const [states, setStates] = useState<IState[]>([] as IState[]);

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        setIsLoading(true);

        addRequiredScripts();
        getCountries();
        getStates();

        setTimeout(() => {
            setIsLoading(false);
        }, 500);
    }, []);

    useEffect(() => {
        formValidations.validateWholeForm();
    }, [errors]);

    useEffect(() => {
        checkoutModel.country && formValidations.validateAddressFields("country", true, /^[\p{L}]{2}$/u);
        checkoutModel.city && formValidations.validateAddressFields("city", true, /^[\p{L}0-9\s-]{2,40}$/u);
        checkoutModel.postcode && formValidations.validateAddressFields("postcode", true, /^[\p{L}0-9\s-]{3,10}$/u);
        checkoutModel.addressLine1 && formValidations.validateAddressFields("addressLine1", true, /^[\p{L}0-9\s,"'.#&/-]{1,50}$/u);
        checkoutModel.addressLine2 && formValidations.validateAddressFields("addressLine2", false, /^[\p{L}0-9\s,"'.#&/-]{0,50}$/u);
        checkoutModel.addressLine3 && formValidations.validateAddressFields("addressLine3", false, /^[\p{L}0-9\s,"'.#&/-]{0,50}$/u);
        checkoutModel.cardNumber && formValidations.validateCardNumber();
        checkoutModel.expiryDate && formValidations.validateExpiryDate();
        checkoutModel.cardholder && formValidations.validateCardholder();
        checkoutModel.securityCode && formValidations.validateCvc();

        formValidations.validateAddressFields("county", checkoutModel.country.toLowerCase() === 'us', /^[\p{L}0-9\s-]{2,}$/u);
    }, [checkoutModel]);

    //#endregion

    //#region HANDLERS

    const handleCardDetailsChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
        const { id, value } = event.target;
        let formattedValue = value;

        let checkoutModelNew = {
            ...checkoutModel
        };

        if (id === 'email-field') {
            formattedValue = formattedValue
                .replace(/[^a-zA-Z0-9._%+-@]/g, '')
                .replace(/[?/;:,<>=]/g, '')
                .trim();
        } else if (id === 'cardNumber-field') {
            formattedValue = formattedValue
                .replace(/\D/g, '')
                .replace(/(\d{4})/g, '$1 ')
                .trim();
        } else if (id === 'expiryDate-field') {
            formattedValue = formattedValue
                .replace(/\D/g, '')
                .replace(/^(\d{2})(\d{1,2})/, '$1 / $2')
                .trim();
        } else if (id === 'securityCode-field') {
            formattedValue = formattedValue
                .replace(/\D/g, '')
                .trim();
        } else if (id === 'country-field') {
            formattedValue = formattedValue
                .replace(/[<>&;?/=]/g, '');

            checkoutModelNew = {
                ...checkoutModelNew,
                county: ""
            };
        } else if (
            id === 'county-field' ||
            id === 'cardholder-field' ||
            id === 'city-field' ||
            id === 'postcode-field' ||
            id === 'addressLine1-field' ||
            id === 'addressLine2-field'
        ) {
            formattedValue = formattedValue
                .replace(/[<>&;?/=]/g, '');
        }

        const propName = id.replace('-field', '');

        if (!formattedValue && propName !== 'addressLine2' && propName !== 'county') {
            setErrors({
                ...errors,
                [propName]: `${propName.charAt(0).toUpperCase() + propName.slice(1)} is required`
            });
        }

        checkoutModelNew = {
            ...checkoutModelNew,
            [propName]: formattedValue
        };

        setCheckoutModel(checkoutModelNew);
    };

    //#endregion

    //#region COMMON HELPERS

    async function getCountries() {
        const countriesJson = await DataService.getCountries(document.documentElement.lang ?? "en-GB");
        const countriesArr = [];

        for (let i = 0; i < countriesJson.length; i++) {
            countriesArr.push({
                name: countriesJson[i].name,
                code: countriesJson[i].code
            } as ICountry);
        }

        setCountries(countriesArr);
    }

    async function getStates() {
        const statesJson = await DataService.getUSAStates();
        const statesArr = [];

        for (let i = 0; i < statesJson.length; i++) {
            statesArr.push({
                name: statesJson[i].name,
                code: statesJson[i].code
            } as IState);
        }

        setStates(statesArr);
    }

    const addRequiredScripts = () => {
        const allScriptSrcs = Array.from(document.querySelectorAll('script')).map(x => x.src);

        if (!allScriptSrcs.some(x => x.includes(elavonSettings.jsScriptUrl))) {
            const script = document.createElement('script');
            script.src = elavonSettings.jsScriptUrl;
            document.body.appendChild(script);
        }

        if (!allScriptSrcs.some(x => x.includes("static/js/elavon-payment.js"))) {
            const script = document.createElement('script');
            script.src = `${window.location.origin}/static/js/elavon-payment.js`;
            document.body.appendChild(script);
        }
    };

    const formValidations = {
        validateWholeForm: () => {
            const isFormValid =
                checkoutModel.country && !errors.country &&
                !errors.county &&
                checkoutModel.city && !errors.city &&
                checkoutModel.postcode && !errors.postcode &&
                checkoutModel.addressLine1 && !errors.addressLine1 &&
                !errors.addressLine2 &&
                checkoutModel.cardNumber && !errors.cardNumber &&
                checkoutModel.cardholder && !errors.cardholder &&
                checkoutModel.expiryDate && !errors.expiryDate &&
                checkoutModel.securityCode && !errors.securityCode;

            props.handleFormUpdate(isFormValid, {
                addressLine1: checkoutModel.addressLine1,
                addressLine2: checkoutModel.addressLine2,
                addressLine3: '',
                city: checkoutModel.city,
                county: checkoutModel.county,
                country: checkoutModel.country,
                postcode: checkoutModel.postcode
            });

            //const paymentCompletedChk = document.getElementById('chkPaymentCompleted') as HTMLInputElement;

            //if (paymentCompletedChk) {
            //    paymentCompletedChk.checked = !isFormValid;
            //    paymentCompletedChk.click();
            //}

            //if (isFormValid) {
            //props.handleModelChange(checkoutModel);
            //}
        },

        validateEmail: () => {
            const value = checkoutModel.email.trim();
            let error = "";

            if (!value) {
                error = "Email is required";
            } else if (!isEmail(value)) {
                error = "Invalid email";
            }

            setErrors(currErrors => ({
                ...currErrors,
                email: error
            }));
        },
        validateAddressFields: (fieldName: string, isRequired: boolean, pattern: RegExp) => {
            const value = checkoutModel[fieldName].trim();
            let error = "";

            if (isRequired && !value) {
                error = `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;

                if (fieldName.toLowerCase() === 'county') {
                    error = "State is required"
                }
            } else if (value && pattern.test(value) === false) {
                error = `Invalid ${fieldName}`;

                if (fieldName.toLowerCase() === 'county' && isRequired) {
                    error = "Invalid state";
                }
            }

            setErrors(currErrors => ({
                ...currErrors,
                [fieldName]: error
            }));
        },
        validateCardNumber: () => {
            const value = checkoutModel.cardNumber.replaceAll(" ", "").trim();
            let error = "";

            if (!value) {
                error = "Card number is required";
            } else if (value.length < 13) {
                error = "Invalid card number";
            }

            setErrors(currErrors => ({
                ...currErrors,
                cardNumber: error
            }));
        },
        validateExpiryDate: () => {
            const value = checkoutModel.expiryDate.trim();
            let error = "";

            if (!value) {
                error = "Expiry date is required";
            } else if (value.length < 7) {
                error = "Invalid expiry date";
            }

            if (!error) {
                const [month, year] = value.split(" / ");
                const monthInt = parseInt(month);
                const yearInt = parseInt(year);

                if (monthInt < 1 || monthInt > 12) {
                    error = "Invalid month";
                } else if (yearInt < 0) {
                    error = "Invalid year";
                }

                if (!error) {
                    const currDate = new Date();
                    const currYear = currDate.getFullYear() % 100; // get the last 2 digits from the year
                    const currMonth = currDate.getMonth() + 1; // JS months are zero-indexed

                    if (yearInt < currYear || (yearInt === currYear && monthInt < currMonth)) {
                        error = "Date is in the past";
                    }
                }
            }

            setErrors(currErrors => ({
                ...currErrors,
                expiryDate: error
            }));
        },
        validateCardholder: () => {
            const value = checkoutModel.cardholder.trim();
            let error = "";

            if (!value || !value.trim()) {
                error = "Cardholder is required";
            } else if (!isFullNameValid(value)) {
                error = "Invalid cardholder";
            }

            setErrors(currErrors => ({
                ...currErrors,
                cardholder: error
            }));
        },
        validateCvc: () => {
            const value = checkoutModel.securityCode.trim();
            let error = "";

            if (!value) {
                error = "Security code is required";
            } else if (value.length < 3 || value.length > 4) {
                error = "Invalid security code";
            }

            setErrors(currErrors => ({
                ...currErrors,
                securityCode: error
            }));
        }
    };

    //#endregion

    return (
        <div id="checkout-container">
            {isLoading ? (
                <LoadingComponent />
            ) : (
                <form id="checkout-form">
                    <h5 style={{ alignSelf: "start", marginTop: "0" }}>
                        {labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Subsection.CardDetails") ?? ""}
                    </h5>
                    <div className="checkout-field-group">
                        <div className="checkout-field-lg">
                            <label className="label-fs">
                                {labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Label.CardNumber") ?? ""}
                            </label>
                            <input
                                id="cardNumber-field"
                                type="text"
                                placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Placeholder.CardNumber") ?? ""}
                                className={errors.cardNumber ? "react-input-error" : ""}
                                value={checkoutModel.cardNumber}
                                onChange={handleCardDetailsChange}
                                maxLength={19}
                            />
                            {errors.cardNumber && <span style={{ color: 'red' }}>{errors.cardNumber}</span>}
                        </div>
                        <div className="checkout-field-sm">
                            <label className="label-fs">
                                {labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Label.ExpiryDate") ?? ""}
                            </label>
                            <input
                                id="expiryDate-field"
                                type="text"
                                placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Placeholder.ExpiryDate") ?? ""}
                                className={errors.expiryDate ? "react-input-error" : ""}
                                value={checkoutModel.expiryDate}
                                onChange={handleCardDetailsChange}
                                maxLength={7}
                            />
                            {errors.expiryDate && <span style={{ color: 'red' }}>{errors.expiryDate}</span>}
                        </div>
                    </div>
                    <div className="checkout-field-group">
                        <div className="checkout-field-lg">
                            <label className="label-fs">
                                {labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Label.Cardholder") ?? ""}
                            </label>
                            <input
                                id="cardholder-field"
                                type="text"
                                placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Placeholder.Cardholder") ?? ""}
                                className={errors.cardholder ? "react-input-error" : ""}
                                value={checkoutModel.cardholder}
                                onChange={handleCardDetailsChange}
                            />
                            {errors.cardholder && <span style={{ color: 'red' }}>{errors.cardholder}</span>}
                        </div>
                        <div className="checkout-field-sm">
                            <label className="label-fs">
                                {labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Label.SecurityCode") ?? ""}
                            </label>
                            <input
                                id="securityCode-field"
                                type="text"
                                placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Placeholder.SecurityCode") ?? ""}
                                className={errors.securityCode ? "react-input-error" : ""}
                                value={checkoutModel.securityCode}
                                onChange={handleCardDetailsChange}
                                maxLength={4}
                            />
                            {errors.securityCode && <span style={{ color: 'red' }}>{errors.securityCode}</span>}
                        </div>
                    </div>
                    <h5 style={{ alignSelf: "start" }}>{labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Subsection.BillingDetails") ?? ""}</h5>
                    <div className="checkout-field-group">
                        <div className="checkout-field">
                            <label className="label-fs">
                                {labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Label.AddressLine1") ?? ""}
                            </label>
                            <input
                                id="addressLine1-field"
                                type="text"
                                placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Placeholder.AddressLine1") ?? ""}
                                className={errors.addressLine1 ? "react-input-error" : ""}
                                value={checkoutModel.addressLine1}
                                onChange={handleCardDetailsChange}
                            />
                            {errors.addressLine1 && <span style={{ color: 'red' }}>{errors.addressLine1}</span>}
                        </div>
                        <div className="checkout-field">
                            <label className="label-fs">
                                {labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Label.AddressLine2") ?? ""}
                            </label>
                            <input
                                id="addressLine2-field"
                                type="text"
                                placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Placeholder.AddressLine2") ?? ""}
                                className={errors.addressLine2 ? "react-input-error" : ""}
                                value={checkoutModel.addressLine2}
                                onChange={handleCardDetailsChange}
                            />
                            {errors.addressLine2 && <span style={{ color: 'red' }}>{errors.addressLine2}</span>}
                        </div>
                    </div>
                    <div className="checkout-field-group">
                        <div className="checkout-field">
                            <label className="label-fs">
                                {labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Label.City") ?? ""}
                            </label>
                            <input
                                id="city-field"
                                type="text"
                                placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Placeholder.City") ?? ""}
                                className={errors.city ? "react-input-error" : ""}
                                value={checkoutModel.city}
                                onChange={handleCardDetailsChange}
                            />
                            {errors.city && <span style={{ color: 'red' }}>{errors.city}</span>}
                        </div>
                        {checkoutModel.country.toLowerCase() === 'us' ? (
                            <div className="checkout-field">
                                <label className="label-fs">
                                    {labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Label.State") ?? ""}
                                </label>
                                <select
                                    id="county-field"
                                    className={errors.county ? "react-input-error" : ""}
                                    value={checkoutModel.county}
                                    onChange={handleCardDetailsChange}
                                >
                                    <option value="">State</option>

                                    {states.map((state, index) => (
                                        <option value={state.code} key={`state_${index}`}>{state.name}</option>
                                    ))}
                                </select>
                                {errors.county && <span style={{ color: 'red' }}>{errors.county}</span>}
                            </div>
                        ) : (
                            <div className="checkout-field">
                                <label className="label-fs">
                                    {labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Label.County") ?? ""}
                                </label>
                                <input
                                    id="county-field"
                                    type="text"
                                    placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Placeholder.County") ?? ""}
                                    className={errors.county ? "react-input-error" : ""}
                                    value={checkoutModel.county}
                                    onChange={handleCardDetailsChange}
                                />
                                {errors.county && <span style={{ color: 'red' }}>{errors.county}</span>}
                            </div>
                        )}
                    </div>
                    <div className="checkout-field-group">
                        <div className="checkout-field">
                            <label className="label-fs">
                                {labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Label.Postcode") ?? ""}
                            </label>
                            <input
                                id="postcode-field"
                                type="text"
                                placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Placeholder.Postcode") ?? ""}
                                className={errors.postcode ? "react-input-error" : ""}
                                value={checkoutModel.postcode}
                                onChange={handleCardDetailsChange}
                            />
                            {errors.postcode && <span style={{ color: 'red' }}>{errors.postcode}</span>}
                        </div>
                        <div className="checkout-field">
                            <label className="label-fs">
                                {labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Label.Country") ?? ""}
                            </label>
                            <select
                                id="country-field"
                                className={errors.country ? "react-input-error" : ""}
                                value={checkoutModel.country}
                                onChange={handleCardDetailsChange}
                            >
                                {countries.map((country, index) => (
                                    <option value={country.code} key={`country_${index}`}>{country.name}</option>
                                ))}
                            </select>
                            {errors.country && <span style={{ color: 'red' }}>{errors.country}</span>}
                        </div>
                    </div>
                    <input type="hidden" id="cardIdentifier-field" />
                </form >
            )}
        </div >
    );
};

export const HandleElavonCheckout = (merchantSession: ICheckoutSessionData) => {
    // @ts-ignore
    // sets cardIdentifier (card tokenisation token) in the hidden input field
    CustomElavonPayment.HandleCreditCardCheckout(merchantSession);
};

export const HandleThreeDSecureAuth = (checkoutRes: IGiftCardCheckoutResult, handleThreeDError: (errorMessage: string) => void) => {
    if (checkoutRes.secure3DAuth &&
        isNotNullOrEmpty(checkoutRes.secure3DAuth.acsUrl) &&
        isNotNullOrEmpty(checkoutRes.secure3DAuth.creq) &&
        isNotNullOrEmpty(checkoutRes.secure3DAuth.transactionid)) {

        redirectToThreeDSecurePage(
            checkoutRes.secure3DAuth.acsUrl,
            checkoutRes.secure3DAuth.creq,
            checkoutRes.secure3DAuth.transactionid
        );
    } else {
        handleThreeDError(`Status from checkout response: ${checkoutRes.statusMessage}`);
    }
};

export default ElavonPayment;

// PRIVATE HELPERS BELOW
function redirectToThreeDSecurePage(acsUrl: string, cReq: string, tranId: string) {
    const redirectForm = document.createElement('form');
    redirectForm.method = 'POST';
    redirectForm.action = acsUrl;

    const cReqInput = document.createElement('input');
    cReqInput.type = 'hidden';
    cReqInput.name = 'creq';
    cReqInput.value = cReq;

    const transactionIdInput = document.createElement('input');
    transactionIdInput.type = 'hidden';
    transactionIdInput.name = 'threeDSSessionData';
    transactionIdInput.value = tranId;

    redirectForm.appendChild(cReqInput);
    redirectForm.appendChild(transactionIdInput);

    const formContainer = document.querySelector("#payment-box #checkout-container");
    formContainer.appendChild(redirectForm);

    redirectForm.submit();
}